import { NOTE_MAX_LENGTH } from "Components/Analyses/constants";
import type { ResourceDictionary } from "Infrastructure/Translations/ResourceDictionary";
import { Resources } from "Infrastructure/Translations/Resources";
import { addMonths, startOfMonth } from "date-fns";
import { format } from "date-fns";
import type { ObjPathProxy } from "ts-object-path";
import { type ObjectSchema, bool, date, mixed, object, string } from "yup";
import { number } from "../Utils/schemaUtils";

export enum DrawType {
  LumpSum = "LumpSum",
  Monthly = "Monthly",
}

export type OtherGoalFormType = {
  readonly name: string;
  readonly drawType: DrawType;
  readonly drawStart: Date;
  readonly referenceStartDate: Date;
  readonly drawDuration?: number;
  readonly drawAmount?: number;
  readonly drawdownInterestRate?: number;
  readonly feeRate: number;
  readonly targetAmount: number;
  readonly monthlyContribution: number;
  readonly monthlyContributionWithFee: number;
  readonly interestRate: number;
  readonly inflationRate: number;
  readonly includeInflation: boolean;
  readonly includeFee: boolean;
  readonly note?: string;
  readonly priority?: boolean;
};

export const minDate = startOfMonth(addMonths(new Date(), 1));

export const getOtherGoalFormSchema: (
  t: (resourcePath: ObjPathProxy<ResourceDictionary, string>, options?: any) => string,
) => ObjectSchema<OtherGoalFormType> = (t) => {
  return object({
    name: string().required(t(Resources.Validation.Required)),
    drawType: mixed<DrawType>().required(t(Resources.Validation.Required)),
    drawStart: date()
      .required(t(Resources.Validation.Required))
      .min(minDate, t(Resources.Validation.MustBeOlderThanDate, { date: format(minDate, "dd.MM.yyyy") })),
    referenceStartDate: date().required(t(Resources.Validation.Required)),
    drawDuration: number().when("drawType", (drawType, schema) => {
      return drawType[0] === DrawType.Monthly
        ? schema.required(t(Resources.Validation.Required)).min(1, t(Resources.Validation.Number.MoreThen, { count: 0 }))
        : schema;
    }),
    drawAmount: number().when("drawType", (drawType, schema) => {
      return drawType[0] === DrawType.Monthly
        ? schema.required(t(Resources.Validation.Required)).min(1, t(Resources.Validation.Number.MoreThen, { count: 0 }))
        : schema;
    }),
    targetAmount: number()
      .required(t(Resources.Validation.Required))
      .min(1, t(Resources.Validation.Number.MoreThen, { count: 0 })),
    monthlyContribution: number().required(t(Resources.Validation.Required)),
    monthlyContributionWithFee: number().required(t(Resources.Validation.Required)),
    feeRate: number().required(t(Resources.Validation.Required)),
    interestRate: number().required(t(Resources.Validation.Required)),
    drawdownInterestRate: number().when("drawType", (drawType, schema) => {
      return drawType[0] === DrawType.Monthly ? schema.required(t(Resources.Validation.Required)) : schema;
    }),
    inflationRate: number().required(t(Resources.Validation.Required)),
    includeInflation: bool().required(t(Resources.Validation.Required)),
    includeFee: bool().required(t(Resources.Validation.Required)),
    note: string().max(NOTE_MAX_LENGTH, t(Resources.Validation.String.Max, { count: NOTE_MAX_LENGTH })),
    priority: bool(),
  }).defined();
};
